import { useMemo } from 'react';
import type { Building, ConstructionStage } from '../../types/core';
import { coordToWorld } from '../../utils/grid';
import { getBuildingDefinition } from '../../world/buildingDefinitions';
import { useGameStore } from '../../simulation/store';

function stageHeight(stage: ConstructionStage, finalHeight: number): number {
  const heights: Record<ConstructionStage, number> = {
    cleared: 0.05,
    foundations: 0.14,
    framework: finalHeight * 0.42,
    scaffolding: finalHeight * 0.72,
    roofing: finalHeight * 0.92,
    completed: finalHeight
  };

  return heights[stage];
}

function buildingHeight(building: Building): number {
  if (building.kind === 'flats') {
    return 1.65 + building.level * 0.12;
  }
  if (building.kind === 'office' || building.kind === 'supermarket') {
    return 1.2;
  }
  if (building.zone === 'commercial') {
    return 0.98;
  }
  if (building.zone === 'workspace') {
    return 1.05;
  }
  return 0.82;
}

function ParkMesh({ building }: { readonly building: Building }) {
  const seed = building.id.length;
  return (
    <group>
      <mesh receiveShadow position={[0, 0.045, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[0.84, 0.84]} />
        <meshStandardMaterial color="#6ea955" roughness={0.9} />
      </mesh>
      <mesh castShadow position={[-0.22, 0.13, 0.18]}>
        <cylinderGeometry args={[0.035, 0.045, 0.18, 6]} />
        <meshStandardMaterial color="#7b5636" />
      </mesh>
      <mesh castShadow position={[-0.22, 0.31, 0.18]}>
        <icosahedronGeometry args={[0.18, 0]} />
        <meshStandardMaterial color={seed % 2 === 0 ? '#5f9866' : '#7aa65a'} roughness={0.9} />
      </mesh>
      <mesh castShadow position={[0.25, 0.08, -0.12]} rotation={[0, Math.PI / 7, 0]}>
        <boxGeometry args={[0.34, 0.08, 0.08]} />
        <meshStandardMaterial color="#a96f43" />
      </mesh>
      <mesh castShadow position={[0.05, 0.07, 0.27]}>
        <torusGeometry args={[0.12, 0.018, 6, 16]} />
        <meshStandardMaterial color="#e6a83f" />
      </mesh>
    </group>
  );
}

function ConstructionDetails({ stage, height }: { readonly stage: ConstructionStage; readonly height: number }) {
  if (stage === 'completed' || stage === 'cleared' || stage === 'foundations') {
    return null;
  }

  const scaffoldHeight = Math.max(0.35, height + 0.15);
  const showRoof = stage === 'roofing';

  return (
    <group>
      {[-0.42, 0.42].map((x) =>
        [-0.42, 0.42].map((z) => (
          <mesh key={`${x}-${z}`} castShadow position={[x, scaffoldHeight / 2, z]}>
            <boxGeometry args={[0.035, scaffoldHeight, 0.035]} />
            <meshStandardMaterial color="#e6a83f" roughness={0.5} />
          </mesh>
        ))
      )}
      <mesh castShadow position={[0, scaffoldHeight * 0.65, -0.44]}>
        <boxGeometry args={[0.95, 0.035, 0.035]} />
        <meshStandardMaterial color="#f3c66f" roughness={0.5} />
      </mesh>
      {showRoof ? (
        <mesh castShadow position={[0, height + 0.16, 0]} rotation={[0, Math.PI / 4, 0]}>
          <coneGeometry args={[0.6, 0.28, 4]} />
          <meshStandardMaterial color="#303942" roughness={0.75} />
        </mesh>
      ) : null}
    </group>
  );
}

function Windows({ building, height }: { readonly building: Building; readonly height: number }) {
  const nightGlow = useGameStore((state) => state.time.minuteOfDay < 6 * 60 || state.time.minuteOfDay > 20 * 60);
  const windowRows = building.kind === 'flats' ? 3 : building.zone === 'commercial' ? 1 : 2;
  const yPositions = Array.from({ length: windowRows }, (_, index) => 0.28 + index * Math.min(0.34, height / 3));

  return (
    <group>
      {yPositions.map((y, row) =>
        [-0.22, 0.22].map((x) => (
          <mesh key={`${row}-${x}`} position={[x, y, -0.463]}>
            <boxGeometry args={[0.14, 0.11, 0.012]} />
            <meshStandardMaterial
              color={nightGlow ? '#f3c66f' : '#d8ecf0'}
              emissive={nightGlow ? '#e6a83f' : '#000000'}
              emissiveIntensity={nightGlow ? 1.2 : 0}
              roughness={0.25}
            />
          </mesh>
        ))
      )}
      {building.zone === 'commercial' ? (
        <mesh position={[0, 0.18, -0.47]}>
          <boxGeometry args={[0.74, 0.17, 0.018]} />
          <meshStandardMaterial color={getBuildingDefinition(building.kind).colours.accent} emissive={nightGlow ? '#7a4b17' : '#000000'} />
        </mesh>
      ) : null}
    </group>
  );
}

function BuildingMesh({ building, size }: { readonly building: Building; readonly size: number }) {
  const definition = getBuildingDefinition(building.kind);
  const setHover = useGameStore((state) => state.setHover);
  const selectBuilding = useGameStore((state) => state.selectBuilding);
  const coord = building.cells[0] ?? { x: 0, z: 0 };
  const position = coordToWorld(coord, size);
  const finalHeight = buildingHeight(building);
  const height = stageHeight(building.stage, finalHeight);
  const completed = building.stage === 'completed';
  const footprint = building.kind === 'supermarket' || building.kind === 'flats' || building.kind === 'office' ? 0.82 : 0.68;

  if (building.zone === 'park') {
    return (
      <group
        position={[position.x, 0.04, position.z]}
        onPointerOver={(event) => {
          event.stopPropagation();
          setHover({ buildingId: building.id, cell: coord });
        }}
        onPointerDown={(event) => {
          event.stopPropagation();
          selectBuilding(building.id);
        }}
      >
        <ParkMesh building={building} />
      </group>
    );
  }

  return (
    <group
      position={[position.x, 0.035, position.z]}
      onPointerOver={(event) => {
        event.stopPropagation();
        setHover({ buildingId: building.id, cell: coord });
      }}
      onPointerDown={(event) => {
        event.stopPropagation();
        selectBuilding(building.id);
      }}
    >
      <mesh castShadow receiveShadow position={[0, height / 2, 0]}>
        <boxGeometry args={[footprint, Math.max(0.05, height), footprint]} />
        <meshStandardMaterial color={definition.colours.wall} roughness={0.86} />
      </mesh>
      {completed || building.stage === 'roofing' ? (
        <mesh castShadow position={[0, height + 0.16, 0]} rotation={[0, Math.PI / 4, 0]}>
          <coneGeometry args={[footprint * 0.78, 0.36, 4]} />
          <meshStandardMaterial color={definition.colours.roof} roughness={0.72} />
        </mesh>
      ) : null}
      {completed ? (
        <>
          <Windows building={building} height={height} />
          <mesh castShadow position={[0.27, height + 0.3, 0.08]}>
            <boxGeometry args={[0.1, 0.3, 0.1]} />
            <meshStandardMaterial color="#8f4a31" roughness={0.8} />
          </mesh>
          <mesh castShadow position={[0, 0.08, -0.48]}>
            <boxGeometry args={[0.17, 0.16, 0.018]} />
            <meshStandardMaterial color={definition.colours.trim} />
          </mesh>
        </>
      ) : null}
      <ConstructionDetails stage={building.stage} height={height} />
    </group>
  );
}

export function BuildingsLayer() {
  const buildings = useGameStore((state) => state.world.buildings);
  const size = useGameStore((state) => state.world.size);
  const visibleBuildings = useMemo(() => buildings, [buildings]);

  return (
    <group>
      {visibleBuildings.map((building) => (
        <BuildingMesh key={building.id} building={building} size={size} />
      ))}
    </group>
  );
}
